import React from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function TalkModal({ show, onClose }) {
  if (!show) return null;

  const instagramUrl = "https://www.instagram.com/mithil_peeris/";
  const linkedinUrl = "https://www.linkedin.com/in/mithil-peeris-285529288/";
  const phoneNumberRaw = "4373293046";
  const phoneDisplay = "437 329 3046";
  const email = "mithil.peeris@gmail.com";

  // WhatsApp needs country code + number (no symbols). Canada = 1
  const whatsappNumber = `1${phoneNumberRaw}`;
  const whatsappMessage = "Hey Mithil, Just saw your website and wanted to reach out and say hi!";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const items = [
    { label: "Instagram", href: instagramUrl, sub: "@mithil_peeris", icon: InstagramIcon },
    { label: "LinkedIn", href: linkedinUrl, sub: "View profile", icon: LinkedInIcon },
    { label: "Phone", href: `tel:${phoneNumberRaw}`, sub: phoneDisplay, icon: PhoneIcon },
    { label: "Email", href: `mailto:${email}`, sub: email, icon: MailIcon },
    { label: "WhatsApp", href: whatsappUrl, sub: "Send message", icon: WhatsAppIcon },
  ];

  return (
    <AnimatePresence>
      {show ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 pt-20 md:pt-24"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            className="relative w-full max-w-md rounded-2xl bg-white p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-2 text-gray-700 hover:bg-gray-100 hover:text-purple-700 focus:outline-none"
              aria-label="Close"
              type="button"
            >
              <span className="text-2xl leading-none">×</span>
            </button>

            <h2 className="mb-4 text-center text-lg font-bold text-purple-700">
              Let’s Connect
            </h2>

            <div className="grid grid-cols-1 gap-3">
              {items.map((it) => {
                const Icon = it.icon;
                const isHttp = it.href.startsWith("http");
                return (
                  <a
                    key={it.label}
                    href={it.href}
                    target={isHttp ? "_blank" : undefined}
                    rel={isHttp ? "noreferrer" : undefined}
                    className="group flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition hover:border-purple-300 hover:bg-purple-50"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 transition group-hover:bg-white">
                      <Icon className="h-6 w-6 text-purple-700" />
                    </span>

                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-semibold text-gray-900">
                        {it.label}
                      </span>
                      <span className="truncate text-xs text-gray-600">
                        {it.sub}
                      </span>
                    </span>

                    <span className="text-sm text-purple-700 opacity-70 transition group-hover:opacity-100">
                      →
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-center hover:border-purple-300 bg-purple-50 rounded-lg border border-gray-200 p-2 transition">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 "
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

/* -------------------------- SVG ICONS -------------------------- */

function InstagramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7.5 2.5h9A5 5 0 0 1 21.5 7.5v9a5 5 0 0 1-5 5h-9a5 5 0 0 1-5-5v-9a5 5 0 0 1 5-5Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M17.3 6.9h.01"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LinkedInIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 9.5v11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.5 6.2a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Z"
        fill="currentColor"
      />
      <path
        d="M10.5 20.5v-7.2c0-2.2 1.2-3.5 3.1-3.5 1.7 0 2.9 1.1 2.9 3.4v7.3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8.2 5.2 10 3.4c.7-.7 1.9-.7 2.6 0l1.8 1.8c.7.7.7 1.9 0 2.6l-1 1c1.1 2.2 2.9 4 5.1 5.1l1-1c.7-.7 1.9-.7 2.6 0l1.8 1.8c.7.7.7 1.9 0 2.6l-1.1 1.1c-1 1-2.4 1.4-3.8 1.1-6.1-1.4-10.9-6.2-12.3-12.3-.3-1.4.1-2.8 1.1-3.8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4.5 7.5h15v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="m5.2 8.2 6.2 5a1 1 0 0 0 1.2 0l6.2-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 21a9 9 0 0 1-4.1-1l-3.4 1 1-3.3A9 9 0 1 1 12 21Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 8.8c.2-.5.6-.6 1-.5l.7.2c.3.1.5.3.6.6l.4 1c.1.3 0 .6-.2.8l-.4.4c.6 1.1 1.5 2 2.6 2.6l.4-.4c.2-.2.5-.3.8-.2l1 .4c.3.1.5.3.6.6l.2.7c.1.4 0 .8-.5 1-.5.3-1.2.4-1.9.2-2.9-.9-5.2-3.2-6.1-6.1-.2-.7-.1-1.4.2-1.9Z"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
  );
}